import type { CartItem, Checkout, Confirmation, Session, ShopContext } from "@agapi/mlc-core";
import { PUBLIC_API } from '$env/static/public';

const API_PREFIX = PUBLIC_API === 'v2' ? 'apiv2' : 'api';

/**
 * Creates a new session
 * @param params - The parameters for creating a session
 * @returns A promise that resolves to the created Session
 */
export const createSession = async (fetch: typeof window.fetch, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session> => {
  const { languageCode, countryCode } = context;
  return (await fetch(`/${API_PREFIX}/session?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'POST'
  })).json();
}

/**
 * Retrieves an existing session
 * @param params - The parameters for getting a session
 * @returns A promise that resolves to the Session or undefined
 */
export const getSession = async (fetch: typeof window.fetch, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session> => {
  const { languageCode, countryCode } = context;
  return (await fetch(`/${API_PREFIX}/session?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'GET'
  })).json();
}

/**
 * Adds an item to the cart
 * @param sessionToken - The session token
 * @param newItem - The item to be added to the cart
 * @returns A promise that resolves to the updated Session
 */
export const addCartItem = async (fetch: typeof window.fetch, newItem: CartItem, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session> => {
  const { languageCode, countryCode } = context;
  const response = await fetch(`/${API_PREFIX}/cart?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'POST',
    body: JSON.stringify(newItem)
  });

  const data = await response.json();
  if (response.ok) {
    return data;
  } else {
    throw data;
  }
}

/**
 * Updates an item in the cart
 * @param sessionToken - The session token
 * @param cartItem - The cart item to be updated
 * @returns A promise that resolves to the updated Session
 */
export const updateCartItem = async (fetch: typeof window.fetch, cartItem: CartItem, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session> => {
  const { languageCode, countryCode } = context;
  const response = await fetch(`/${API_PREFIX}/cart?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'PUT',
    body: JSON.stringify(cartItem)
  });

  const data = await response.json();
  if (response.ok) {
    return data;
  } else {
    throw data;
  }
}

/**
 * Removes an item from the cart
 * @param sessionToken - The session token
 * @param cartItem - The cart item to be removed
 * @returns A promise that resolves to the updated Session
 */
export const removeCartItem = async (fetch: typeof window.fetch, cartItem: Pick<CartItem, "id" | "sku">, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session> => {
  const { languageCode, countryCode } = context;
  const response = await fetch(`/${API_PREFIX}/cart?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'DELETE',
    body: JSON.stringify(cartItem)
  });

  const data = await response.json();
  if (response.ok) {
    return data;
  } else {
    throw data;
  }
}

/**
 * Initiates the checkout process
 * @param sessionToken - The session token
 * @param options - Additional options for checkout
 * @returns A promise that resolves to the Checkout or undefined
 */
export const checkout = async (fetch: typeof window.fetch, options: unknown, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Checkout> => {
  const { languageCode, countryCode } = context;
  const response = await fetch(`/${API_PREFIX}/checkout?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'POST',
    body: JSON.stringify(options)
  });

  const data = await response.json();
  if (response.ok) {
    return data;
  } else {
    throw data;
  }
}

/**
 * Retrieves the confirmation for a completed checkout
 * @param sessionToken - The session token
 * @returns A promise that resolves to the Confirmation
 */
export const confirmation = async (fetch: typeof window.fetch, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Confirmation> => {
  const { languageCode, countryCode } = context;
  return (await fetch(`/${API_PREFIX}/checkout/confirmation?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'GET'
  })).json();
}

/**
 * Adds a discount code to the cart
 * @param discountCode - The discount code to be added
 * @param context - The context of the shop
 * @returns A promise that resolves to the updated Session or undefined
 */
export const addDiscountCode = async (fetch: typeof window.fetch, discountCode: string, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session | undefined> => {
  const { languageCode, countryCode } = context;
  const response = await fetch(`/${API_PREFIX}/cart/discount-codes?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'POST',
    body: JSON.stringify({ discountCode })
  });
  return response.ok ? await response.json() : undefined;
}

/**
 * Removes a discount code from the cart
 * @param discountCode - The discount code to be removed
 * @param context - The context of the shop
 * @returns A promise that resolves to the updated Session or undefined
 */
export const removeDiscountCode = async (fetch: typeof window.fetch, discountCode: string, context: Pick<ShopContext, 'countryCode' | 'languageCode'>): Promise<Session | undefined> => {
  const { languageCode, countryCode } = context;
  const response = await fetch(`/${API_PREFIX}/cart/discount-codes/${discountCode}?languageCode=${languageCode}&countryCode=${countryCode}`, {
    method: 'DELETE'
  });
  return response.ok ? await response.json() : undefined;
}
